import Anthropic from '@anthropic-ai/sdk';

// AI features are DISABLED by default so no provider key is shipped to the
// browser. Set REACT_APP_AI_FEATURES_ENABLED=true AND provide
// REACT_APP_IP_DASHBOARD_ANTHROPIC_API_KEY to re-enable. When disabled, every
// exported function short-circuits to a disabled result and the key env var is
// never even read (so it is never bundled).
const AI_FEATURES_ENABLED = process.env.REACT_APP_AI_FEATURES_ENABLED === 'true';

// Lazily construct the client only when enabled AND a key is present.
let anthropicClient = null;
function getClient() {
  if (!AI_FEATURES_ENABLED) return null;
  if (!process.env.REACT_APP_IP_DASHBOARD_ANTHROPIC_API_KEY) return null;
  if (!anthropicClient) {
    anthropicClient = new Anthropic({
      apiKey: process.env.REACT_APP_IP_DASHBOARD_ANTHROPIC_API_KEY,
      dangerouslyAllowBrowser: true, // Required for browser usage
    });
  }
  return anthropicClient;
}

/**
 * General purpose AI analysis function
 * @param {string} prompt - The prompt to send to Claude
 * @param {string} imageData - Optional base64 image data
 * @returns {Promise<Object>} - Response object with success status and data
 */
export const analyzePrices = async (prompt, imageData = null) => {
  const client = getClient();
  if (!client) {
    return { success: false, error: 'AI features are disabled.' };
  }
  try {
    const messages = [{
      role: "user",
      content: imageData 
        ? [
            { type: "text", text: prompt },
            {
              type: "image",
              source: {
                type: "base64",
                media_type: "image/png",
                data: imageData
              }
            }
          ]
        : [{ type: "text", text: prompt }]
    }];

    const response = await client.messages.create({
      model: "claude-3-5-sonnet-20241022",
      max_tokens: 4096,
      messages: messages
    });

    return {
      success: true,
      data: response.content[0].text
    };
  } catch (error) {
    console.error("Anthropic API Error:", error);
    return {
      success: false,
      error: error.message || "Failed to connect to AI service"
    };
  }
};

/**
 * Analyze campsite pricing (simulated - real scraping requires backend)
 * @param {string} url - The campsite URL
 * @param {string} checkinDate - Check-in date
 * @param {string} checkoutDate - Check-out date
 * @returns {Promise<Object>} - Pricing analysis results
 */
export const scrapeCampsitePrices = async (url, checkinDate, checkoutDate) => {
  const prompt = `
I need you to help analyze campsite pricing information.

Website URL: ${url}
Check-in Date: ${checkinDate}
Check-out Date: ${checkoutDate}

Since direct web scraping requires backend infrastructure with Computer Use API, please provide a realistic SAMPLE pricing analysis based on typical campsite pricing patterns for demonstration purposes.

Please provide the information in this exact JSON format (return ONLY the JSON, no other text):
{
  "success": true,
  "sites": [
    {"type": "Premium RV Site with Full Hookups", "price": "$45/night", "available": true},
    {"type": "Standard RV Site with Electric", "price": "$35/night", "available": true},
    {"type": "Tent Site - Waterfront", "price": "$28/night", "available": false},
    {"type": "Tent Site - Standard", "price": "$22/night", "available": true},
    {"type": "Cabin Rental", "price": "$85/night", "available": true}
  ],
  "total_sites_found": 5
}

Make the sample data realistic and varied in pricing and availability.
`;

  try {
    const response = await analyzePrices(prompt);
    
    if (response.success) {
      // Try to extract JSON from Claude's response
      const jsonMatch = response.data.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        return parsed;
      }
    }
    
    return response;
  } catch (error) {
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * General AI chat for price-related queries
 * @param {string} question - User's question about pricing
 * @returns {Promise<Object>} - AI response
 */
export const askPricingQuestion = async (question) => {
  const prompt = `
You are a helpful assistant for campsite pricing analysis. Answer the following question:

${question}

Provide a clear, concise answer focused on camping and pricing topics.
`;

  return await analyzePrices(prompt);
};


